import React, { useState } from 'react';
import { bookingApi } from '../../utils/bookingApi';
import { formatDate } from '../../utils/dates';
import { Sheet, useConfirm, useToast } from '../ui';

const errorMessage = (err, fallback) => err.response?.data?.error || fallback;
const latest = (signatures, kind) => [...(signatures || [])].reverse().find(s => s.kind === kind);

function SignatureLine({ label, signature, contentHash, unsigned }) {
  if (!signature) return <p className="wsig-line wsig-line--none"><strong>{label}:</strong> {unsigned}</p>;
  const upToDate = signature.contentHash === contentHash;
  return (
    <p className={`wsig-line ${upToDate ? 'wsig-line--ok' : 'wsig-line--stale'}`}>
      <strong>{label}:</strong> {signature.fullName}, {formatDate(signature.signedAt)}
      {!upToDate && ". The report has changed since, so it needs signing again."}
    </p>
  );
}

// A button that opens a pop-up to sign by typing your full name
function SignButton({ buttonLabel, onSign }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const close = () => { setOpen(false); setName(''); setError(null); };
  const submit = async () => {
    if (name.trim().length < 2) return;
    setBusy(true);
    setError(null);
    const problem = await onSign(name.trim());
    setBusy(false);
    if (problem) setError(problem);
    else close();
  };
  return (
    <>
      <button type="button" className="bk-btn bk-btn--primary" onClick={() => setOpen(true)}>{buttonLabel}</button>
      {open && (
        <Sheet
          title={buttonLabel}
          onClose={close}
          dirty={!!name.trim()}
          onSubmit={submit}
          footer={(requestClose) => (
            <div className="bk-actions">
              <button type="submit" className="bk-btn bk-btn--primary" disabled={busy || name.trim().length < 2}>{busy ? 'Signing…' : 'Sign'}</button>
              <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
            </div>
          )}
        >
          <label className="bk-label">
            Type your full name to sign this report as it is now
            <input className="bk-input" value={name} onChange={e => setName(e.target.value)} autoComplete="name" />
          </label>
          {error && <p className="bk-form-error" role="alert">{error}</p>}
        </Sheet>
      )}
    </>
  );
}

// The person who filed the report signs it; a Welfare Officer or club admin
// signs it off once there's an outcome. A signature shows as out of date when
// the report has changed since.
export function SignaturesPanel({ report, currentUserId, onChange }) {
  const toast = useToast();
  const filer = latest(report.signatures, 'FILER');
  const signOff = latest(report.signatures, 'SIGN_OFF');
  const isFiler = report.reportedBy?.id === currentUserId;
  const filedBy = `${report.reportedBy.firstName} ${report.reportedBy.lastName}`;

  // Gives back a problem to show, or nothing when it worked
  const sign = kind => async (fullName) => {
    try {
      const res = await bookingApi.signWelfareReport(report.id, kind, fullName);
      onChange(res.data);
      toast(kind === 'FILER' ? 'Report signed' : 'Report signed off');
      return null;
    } catch (err) {
      return errorMessage(err, "Couldn't sign. Try again.");
    }
  };

  const canSignAsFiler = isFiler && filer?.contentHash !== report.contentHash;
  const needsSignOff = signOff?.contentHash !== report.contentHash;

  return (
    <div className="wsig">
      <SignatureLine label="Signed by the person who filed it" signature={filer} contentHash={report.contentHash} unsigned={`not yet signed by ${filedBy}`} />
      <SignatureLine label="Signed off" signature={signOff} contentHash={report.contentHash} unsigned="not yet signed off" />
      {needsSignOff && !report.outcome?.trim() && <p className="bk-muted wsig-hint">Record the outcome to sign the report off.</p>}
      {(canSignAsFiler || (needsSignOff && report.outcome?.trim())) && (
        <div className="bk-actions">
          {canSignAsFiler && <SignButton buttonLabel="Sign as the person who filed it" onSign={sign('FILER')} />}
          {needsSignOff && report.outcome?.trim() && <SignButton buttonLabel="Sign off" onSign={sign('SIGN_OFF')} />}
        </div>
      )}
    </div>
  );
}

function shareStatus(share) {
  if (share.withdrawnAt) return { className: 'bk-muted', text: `Withdrawn ${formatDate(share.withdrawnAt)}` };
  if (share.acknowledgedAt) return { className: 'wshare-ok', text: `Read and signed by ${share.acknowledgedName}, ${formatDate(share.acknowledgedAt)}` };
  return { className: 'wshare-waiting', text: 'Not read yet' };
}

const fileCount = n => (n ? ` and its ${n === 1 ? 'file' : `${n} files`}` : '');
const wholeReportLabel = s => `The whole report${fileCount(s.attachmentIds?.length)}`;

// The pop-up for sharing: who with, and either a message or the whole report
function ShareSheet({ report, onClose, onShared }) {
  const [recipients, setRecipients] = useState(null);
  const [recipientId, setRecipientId] = useState('');
  const [message, setMessage] = useState(report.outcome || '');
  const [wholeReport, setWholeReport] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    bookingApi.getWelfareShareRecipients(report.id)
      .then(res => { setRecipients(res.data); setRecipientId(res.data[0]?.id || ''); })
      .catch(err => setError(errorMessage(err, "Couldn't load who to share with.")));
  }, [report.id]);

  const share = async () => {
    if (!recipientId || (!wholeReport && !message.trim())) return;
    setBusy(true);
    setError(null);
    try {
      const res = await bookingApi.shareWelfareOutcome(report.id, recipientId, message.trim(), wholeReport);
      onShared(res.data);
    } catch (err) {
      setError(errorMessage(err, "Couldn't share. Try again."));
      setBusy(false);
    }
  };

  const ready = recipients?.length > 0;
  return (
    <Sheet
      title="Share"
      onClose={onClose}
      dirty={message !== (report.outcome || '') && !!message.trim()}
      onSubmit={ready ? share : undefined}
      footer={(requestClose) => (ready ? (
        <div className="bk-actions">
          <button type="submit" className="bk-btn bk-btn--primary" disabled={busy || !recipientId || (!wholeReport && !message.trim())}>{busy ? 'Sharing…' : 'Share'}</button>
          <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
        </div>
      ) : (
        <div className="bk-actions"><button type="button" className="bk-btn" onClick={requestClose}>Close</button></div>
      ))}
    >
      {recipients?.length === 0 && (
        <p className="bk-muted">There's nobody to share this with in the app. Reports about a gymnast are shared with a parent on their account; reports about an adult are shared with that person.</p>
      )}
      {ready && (
        <div className="wshare-form">
          <label className="bk-label">
            Share with
            <select className="bk-input" value={recipientId} onChange={e => setRecipientId(e.target.value)}>
              {recipients.map(r => <option key={r.id} value={r.id}>{r.firstName} {r.lastName} ({r.relation})</option>)}
            </select>
          </label>
          <fieldset className="wshare-choice">
            <legend className="bk-label">What to share</legend>
            <label>
              <input type="radio" name="wshare-what" checked={!wholeReport} onChange={() => { setWholeReport(false); if (!message.trim()) setMessage(report.outcome || ''); }} />
              A message
            </label>
            <label>
              <input type="radio" name="wshare-what" checked={wholeReport} onChange={() => { setWholeReport(true); if (message === (report.outcome || '')) setMessage(''); }} />
              The whole report{fileCount(report.attachments?.length)}
            </label>
          </fieldset>
          <label className="bk-label">
            {wholeReport ? <>A note to go with it<span className="bk-optional"> (optional)</span></> : 'Message'}
            <textarea className="bk-input" rows={wholeReport ? 3 : 5} value={message} onChange={e => setMessage(e.target.value)} />
          </label>
          <p className="bk-muted wsig-hint">
            {wholeReport
              ? "They'll see the report as it is now, including who filed it, the witness's name and statement, and the attached files. The witness's contact details aren't shared. Later changes to the report aren't shown to them. They'll be asked to sign it as read."
              : "Only this message is shared. They won't see anything else in the report. They'll be asked to sign it as read."}
          </p>
        </div>
      )}
      {error && <p className="bk-form-error" role="alert">{error}</p>}
    </Sheet>
  );
}

// Share with the person the report relates to, or a gymnast's parent: either
// a message written here, or the whole report and its files as they are now,
// with an optional note.
export function SharePanel({ report, onChange }) {
  const confirm = useConfirm();
  const toast = useToast();
  const [composing, setComposing] = useState(false);
  const [error, setError] = useState(null);
  const shares = report.shares || [];

  const withdraw = async (s) => {
    const ok = await confirm({
      title: `Withdraw this from ${s.recipient.firstName}?`,
      message: "They won't be able to read it any more.",
      confirmLabel: 'Withdraw',
    });
    if (!ok) return;
    setError(null);
    try {
      const res = await bookingApi.withdrawWelfareShare(report.id, s.id);
      onChange(res.data);
      toast('Share withdrawn');
    } catch (err) {
      setError(errorMessage(err, "Couldn't withdraw. Try again."));
    }
  };

  return (
    <div className="wshare">
      {shares.map(s => {
        const status = shareStatus(s);
        return (
          <div key={s.id} className={`wshare-item${s.withdrawnAt ? ' wshare-item--withdrawn' : ''}`}>
            <div className="wshare-item__head">
              <strong>{s.recipient.firstName} {s.recipient.lastName}</strong>
              <span className="bk-muted">Shared {formatDate(s.sharedAt)} by {s.sharedBy.firstName}</span>
            </div>
            {s.includesReport && <p className="wshare-item__what">{wholeReportLabel(s)}</p>}
            {s.message && <p className="wshare-item__message">{s.message}</p>}
            <div className="wshare-item__status">
              <span className={status.className}>{status.text}</span>
              {!s.acknowledgedAt && !s.withdrawnAt && (
                <button type="button" className="bk-btn bk-btn--sm bk-btn--outline-danger" onClick={() => withdraw(s)}>Withdraw</button>
              )}
            </div>
          </div>
        );
      })}
      {error && <p className="bk-form-error" role="alert">{error}</p>}
      <div className="bk-actions">
        <button type="button" className="bk-btn" onClick={() => setComposing(true)}>Share</button>
      </div>
      {composing && (
        <ShareSheet
          report={report}
          onClose={() => setComposing(false)}
          onShared={(updated) => { setComposing(false); onChange(updated); toast('Shared'); }}
        />
      )}
    </div>
  );
}
